import { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'

import type { FinanceiroOrdem, ModoAtualizar } from '../domain/financeiro'
import { OrdemFinanceiroService } from '../services/ordem-financeiro.service'

export const chaveOrdemFinanceiro = (orderId: number | string) => [
  'ordem-financeiro',
  Number(orderId),
]

interface Opcoes {
  // Para o polling de uma cobrança em processamento.
  refetchInterval?: number | false
  enabled?: boolean
}

// A chave é a mesma para os três modos de `atualizar`: o selo do cabeçalho e a
// tab compartilham o cache, e o resultado de uma atualização aparece nos dois.
export const useOrdemFinanceiro = (
  orderId: number | string | undefined,
  atualizar: ModoAtualizar,
  { refetchInterval, enabled = true }: Opcoes = {},
) => {
  const queryClient = useQueryClient()
  const id = Number(orderId)
  const queryKey = chaveOrdemFinanceiro(id)
  const valido = Number.isInteger(id) && id > 0

  const query = useQuery(
    queryKey,
    () => OrdemFinanceiroService.buscar(id, atualizar),
    {
      enabled: enabled && valido,
      // Dado financeiro: bem mais curto que os 10 min padrão do queryClient.
      staleTime: 30 * 1000,
      // 429 e 502 não melhoram com retentativa automática.
      retry: false,
      refetchInterval,
    },
  )

  const invalidate = () => queryClient.invalidateQueries(queryKey)

  const [atualizando, setAtualizando] = useState(false)

  // Relê da Conta Azul (`sempre` por padrão) e grava o resultado na chave
  // compartilhada, para o selo e a tab não divergirem. Um erro HTTP mantém o
  // dado que já estava na tela (o toast vem do interceptor).
  const atualizarAgora = async (modo: ModoAtualizar = 'sempre') => {
    if (!valido) return undefined

    setAtualizando(true)
    try {
      // Uma leitura local (`nao`) ainda em voo chegaria depois desta e a
      // sobrescreveria com dado mais velho.
      await queryClient.cancelQueries(queryKey)
      const dados = await OrdemFinanceiroService.buscar(id, modo)
      queryClient.setQueryData<FinanceiroOrdem>(queryKey, dados)
      return dados
    } catch {
      return undefined
    } finally {
      setAtualizando(false)
    }
  }

  return { query, invalidate, atualizarAgora, atualizando }
}
